import { useState, useEffect } from "react";
import { DateTime } from "luxon";

import usePartDetails from "../../../data/usePartDetails";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { IEbayOffer } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";
import { ResourceType } from "../../../models/Note";
import { useQueryClient } from '@tanstack/react-query';
import { PageMode } from "../../../models/PagedTable";
import checkEbayDisabled from "../../../utils/checkEbayDisabled";
import formatDateTime from '../../../utils/formatDateTime';
import ContactFromId from "../../contacts/ContactFromId";
import EditListingImages from "../EditListingImages";
import EditListingSpecifics from "../EditListingSpecifics";
import EbayListingTitle, { computeEbayListingTitle } from "../EbayListingTitle";
import { EbayTableCategoryDropdown } from "./EbayTableCategoryDropdown";
import NotesOutput from "../../shared/Notes/NotesOutput";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import openInNewTab from "../../../utils/openInNewTab";
import UnlistedEbaySearchButtons from "./UnlistedEbaySearchButtons";
import Card from "../../shared/Card/Card";
import LoadingWheel from "../../shared/LoadingWheel";
import SendToQcModal from "../../stock/SendToQcModal";
import UnlistedDetailsCard from "../../shared/Unlisted/UnlistedDetailsCard";


interface ExpandedRowContentProps {
  part: IPart;
}

export default function UnlistedExpandedRowContent({ part }: ExpandedRowContentProps) {
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Part");

  // Fetch full part details when row is expanded
  const { partDetails, isLoading: isLoadingDetails, isError: isErrorDetails } = usePartDetails(part.id, true);

  // Use full details if available, otherwise fall back to table row data
  const fullPart = partDetails || part;

  const [photosOpen, setPhotosOpen] = useState<boolean>(false);
  const [aspectsOpen, setAspectsOpen] = useState<boolean>(false);
  const [qcOpen, setQcOpen] = useState<boolean>(false);

  // Load card open/closed preference from localStorage (synchronous read for initial state)
  const getInitialCardState = (): boolean => {
    if (typeof window !== 'undefined') {
      const savedPreference = localStorage.getItem('unlistedExpandedRowCardDefaultOpen');
      return savedPreference !== null ? savedPreference === 'true' : false;
    }
    return false;
  };

  const [cardDefaultOpen, setCardDefaultOpen] = useState<boolean>(getInitialCardState());

  const currentTitle = computeEbayListingTitle(fullPart);

  // Save card open/closed preference to localStorage when toggled
  const handleCardToggle = (isOpen: boolean) => {
    localStorage.setItem('unlistedExpandedRowCardDefaultOpen', isOpen.toString());
    setCardDefaultOpen(isOpen);
  };

  // Sort images by order
  if (fullPart.images && fullPart.images.length > 0) {
    fullPart.images.sort((a, b) => {
      if (a.order === null) {
        return 1;
      }

      if (b.order === null) {
        return -1;
      }

      return a.order - b.order;
    });
  }

  let offer: IEbayOffer = {
    ...fullPart.offers[0] as IEbayOffer,
  };

  // Show loading state while fetching full details
  if (isLoadingDetails && !partDetails) {
    return (
      <div className="flex justify-center items-center p-8">
        <LoadingWheel />
        <span className="ml-4 text-gray-600">Loading part details...</span>
      </div>
    );
  }

  // Show error state if details failed to load (but still show partial data)
  if (isErrorDetails && !partDetails) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-800">Unable to load full part details. Showing limited information.</p>
      </div>
    );
  }

  return (
    <>
      <SendToQcModal
        open={qcOpen}
        setOpen={setQcOpen}
        parts={[fullPart]}
        onCompleted={() => {
          saveHandlers.onSuccess();
          queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
        }}
        onFailed={saveHandlers.onError}
      />
      <Modal key={`photos-${fullPart.id}`} open={photosOpen} setOpen={setPhotosOpen} width={"max-w-7xl"}>
        <EditListingImages part={fullPart} />
      </Modal>
      <Modal key={`aspects-${fullPart.id}`} open={aspectsOpen} setOpen={setAspectsOpen} width={"max-w-7xl"}>
        <EditListingSpecifics part={fullPart} />
      </Modal>
      <div className="grid grid-cols-1 gap-4 p-2 w-full">

        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <UnlistedDetailsCard fullPart={fullPart} />
        </div>

        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-y-2">
            <UnlistedEbaySearchButtons part={fullPart} />
            <div className="space-x-4">
              <ButtonInput label="Send To QC" isSubmit={false} onClick={() => setQcOpen(true)} classes={""} />
              <ButtonInput label="Photo Manager" isSubmit={false} onClick={() => setPhotosOpen(true)} classes={""} />
              <ButtonInput label="Aspects Manager" isSubmit={false} onClick={() => setAspectsOpen(true)} classes={""} />
            </div>
          </div>

          <div className="flex justify-end">
            <EbayTableCategoryDropdown part={fullPart} categoryId={fullPart.offers[0]?.categoryId} classes="min-w-[30vw]" />
          </div>
        </div>

        <hr />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-2 divide-x">
          <div className="px-2">
            <h1 className="text-lg font-semibold">Part Condition Notes</h1>
            <h2 className="text-gray-700">This will go into the listing</h2>
            <div>
              <NotesOutput
                resourceId={fullPart.id}
                resourceType={ResourceType.Part}
                shouldScroll={false}
                notes={fullPart.notes ? fullPart.notes : []}
                refetchQuery={["pagedTable"]}
              />
            </div>
          </div>
          <div className="px-2">
            <h1 className="text-lg font-semibold">Internal Part Notes</h1>
            <h2 className="text-gray-700">This will not go onto the listing</h2>
            <div>
              <NotesOutput
                resourceId={fullPart.id}
                resourceType={ResourceType.InternalPart}
                shouldScroll={false}
                notes={fullPart.internalNotes ? fullPart.internalNotes : []}
                refetchQuery={["pagedTable"]}
              />
            </div>
          </div>
        </div>

      </div>
    </>
  );
} 